import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Button } from '@viastud/ui/button'
import { useToast } from '@viastud/ui/hooks/use-toast'
import { Input } from '@viastud/ui/input'
import { trpc } from '@viastud/ui/lib/trpc'
import { PasswordInput } from '@viastud/ui/password-input'
import { EmptyState } from '@viastud/ui/shared/empty-state'
import { CircleAlert } from 'lucide-react'
import type { FormEvent } from 'react'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { useShallow } from 'zustand/shallow'

import { useAuthStore } from '@/store/auth.store'

const searchSchema = z.object({
  from: z.string().optional(),
})

export const Route = createFileRoute('/login')({
  validateSearch: (search) => searchSchema.parse(search),
  component: Login,
})

function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isError, setIsError] = useState(false)
  const loginMutation = trpc.userAuth.login.useMutation()
  const { toast } = useToast()

  const { storedRole, updateAuth } = useAuthStore(
    useShallow((state) => ({
      storedRole: state.role,
      updateAuth: state.updateAuth,
    }))
  )

  const navigate = useNavigate({ from: '/login' })
  const { from } = Route.useSearch()
  const isBeforeOpenDate = new Date() < new Date('2025-09-22T09:00:00+02:00')

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      const { user, role } = await loginMutation.mutateAsync({
        email,
        password,
      })

      if (role === 'PARENT') {
        toast({
          description: 'Aucun utilisateur trouvé.',
          variant: 'destructive',
        })
        updateAuth({
          user: undefined,
          role: null,
          isAuthenticated: false,
        })
        return
      }

      updateAuth({
        user,
        role,
        isAuthenticated: true,
      })
    } catch {
      setIsError(true)
      toast({
        description: 'Vérifiez les informations de compte que vous avez saisies et réessayez',
        variant: 'destructive',
      })
    }
  }

  useEffect(() => {
    if (storedRole === 'PARENT') {
      if (from?.startsWith('/parent')) {
        void navigate({ to: from })
      } else {
        void navigate({ to: '/parent' })
      }
    }
    if (storedRole === 'STUDENT') {
      if (from && !from.startsWith('/parent')) {
        void navigate({ to: from })
      } else {
        void navigate({ to: '/' })
      }
    }
  }, [storedRole, navigate, from])

  if (isBeforeOpenDate) {
    return (
      <div className="shadow-custom my-auto flex w-[480px] flex-col items-stretch justify-center gap-8 rounded-2xl bg-white p-8">
        <div className="flex flex-col items-center">
          <img src="/logos/viastud-logo.svg" alt="viastud logo" className="size-12" />
          <div className="flex flex-col items-center gap-1">
            <h1 className="text-[28px] text-gray-950">Ouverture le 22 septembre</h1>
          </div>
        </div>
        <EmptyState
          image={
            <img src="/photos/dashboard_no_lessons.svg" alt="viastud" className="h-full w-full" />
          }
          title="Bienvenue chez Viastud !"
          message={
            <div className="flex flex-col items-center gap-2">
              <p>La plateforme n&apos;est pas encore ouverte.</p>
              <p>Elle ouvrira ses portes le lundi 22 septembre.</p>
              <p>À très bientôt !</p>
            </div>
          }
        />
      </div>
    )
  }

  return (
    <div className="shadow-custom my-auto flex w-[400px] flex-col items-stretch justify-center gap-8 rounded-2xl bg-white p-6">
      <div className="flex flex-col items-center">
        <img src="/logos/viastud-logo.svg" alt="viastud logo" className="size-12" />
        <div className="flex flex-col items-center gap-1">
          <h1 className="text-[32px] text-gray-950">Se connecter</h1>
        </div>
      </div>
      {isError && (
        <div className="flex items-center self-stretch rounded-lg bg-gray-100 p-4">
          <p className="flex text-sm font-medium text-gray-950">
            Votre adresse e-mail et/ou mot de passe sont incorrects.
          </p>
        </div>
      )}
      <form className="flex flex-col items-stretch gap-6" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-[20px]">
          <div className="flex flex-col gap-1.5">
            <p className="text-sm font-medium text-gray-700">E-mail</p>
            <Input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
              }}
              placeholder="Entrez votre e-mail"
              error={isError}
              required
              buttonContent={isError && <CircleAlert className="size-5" color="#ef4444" />}
            />
          </div>
          <div className="flex flex-col items-stretch gap-4">
            <div className="flex flex-col gap-1.5">
              <p className="text-sm font-medium text-gray-700">Mot de passe</p>
              <PasswordInput
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                }}
                placeholder="Entrez votre mot de passe"
                error={isError}
                required
              />
            </div>
            <div className="flex flex-col items-start">
              <Button variant="none">
                <Link to="/forgotten-password">
                  <p className="text-sm font-semibold text-blue-800">Mot de passe oublié</p>
                </Link>
              </Button>
            </div>
          </div>
        </div>
        <Button type="submit">Se connecter</Button>
      </form>
      {/*<div className="flex flex-col gap-4">
        <div className="relative flex items-center">
          <div className="flex-grow border-t border-gray-200" />
          <span className="mx-4 flex-shrink text-xs text-gray-400 uppercase">Ou</span>
          <div className="flex-grow border-t border-gray-200" />
        </div>
        <Button variant="outline" type="button" onClick={handleGoogleSignIn} className="gap-2">
          <svg className="h-4 w-4" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
            <path d="M1 1h22v22H1z" fill="none" />
          </svg>
          Continuer avec Google
        </Button>
      </div>*/}
      <div className="flex flex-col items-center gap-2">
        <p className="text-sm text-gray-600">Vous n&apos;avez pas encore de compte ?</p>
        <Link to="/onboarding">
          <p className="text-sm font-semibold text-blue-800">S&apos;inscrire</p>
        </Link>
      </div>
    </div>
  )
}
